// Guided capture of a photo of a paper report. A blurry photo cannot be fixed afterwards (the detail was never captured), so the camera
// itself coaches: a page-shaped frame to fill, a live sharpness reading, a capture button that wakes up when the page is in focus, and a
// short burst of frames of which only the sharpest is kept. Never blocks: "Capture anyway" is always there, and the photo then goes through
// the same quality check and the navigator's check as any upload. Falls back (no camera, no permission, plain http) to the device's camera
// app through an ordinary file input.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MIN_SHARPNESS, photoProblems, pickSharpest, sharpnessOf } from '../../utils/photoQuality';

const BURST = 3;
const BURST_GAP_MS = 220;
const METER_MS = 400;
const JPEG_QUALITY = 0.92;

export const canCaptureLive = () =>
  typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && (typeof window === 'undefined' || window.isSecureContext !== false);

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

function frameOf(video) {
  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  canvas.getContext('2d').drawImage(video, 0, 0);
  return canvas;
}

const toFile = (canvas) => new Promise((resolve) => canvas.toBlob(
  (blob) => resolve(new File([blob], `report-photo-${Date.now()}.jpg`, { type: 'image/jpeg' })), 'image/jpeg', JPEG_QUALITY));

export default function PhotoCapture({ onCapture, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fallbackRef = useRef(null);
  const [state, setState] = useState('starting');           // starting | live | error
  const [sharp, setSharp] = useState(null);                 // live reading, smoothed
  const [busy, setBusy] = useState(false);
  const [shot, setShot] = useState(null);                   // { file, url, problems }

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const start = useCallback(async () => {
    setState('starting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 2560 }, height: { ideal: 1440 } }, audio: false,
      });
      streamRef.current = stream;
      const v = videoRef.current;
      if (!v) { stop(); return; }
      v.srcObject = stream;
      await v.play();
      setState('live');
    } catch {
      setState('error');
    }
  }, [stop]);

  useEffect(() => {
    if (canCaptureLive()) start(); else setState('error');
    return stop;
  }, [start, stop]);

  // the live reading: a few frames a second, smoothed so one shaky frame does not flicker the meter
  useEffect(() => {
    if (state !== 'live' || shot) return undefined;
    const recent = [];
    const t = setInterval(() => {
      const v = videoRef.current;
      if (!v || !v.videoWidth) return;
      try {
        recent.push(sharpnessOf(v, v.videoWidth, v.videoHeight));
        if (recent.length > 3) recent.shift();
        setSharp(recent.reduce((a, b) => a + b, 0) / recent.length);
      } catch { /* a frame that cannot be read is just skipped */ }
    }, METER_MS);
    return () => clearInterval(t);
  }, [state, shot]);

  const capture = async () => {
    const v = videoRef.current;
    if (!v || busy) return;
    setBusy(true);
    try {
      const frames = [];
      for (let i = 0; i < BURST; i++) {
        const canvas = frameOf(v);
        frames.push({ canvas, sharpness: sharpnessOf(canvas, canvas.width, canvas.height) });
        if (i < BURST - 1) await wait(BURST_GAP_MS);
      }
      const best = frames[pickSharpest(frames)];
      const file = await toFile(best.canvas);
      setShot({ file, url: URL.createObjectURL(file),
        problems: photoProblems({ width: best.canvas.width, height: best.canvas.height, sharpness: best.sharpness }) });
    } finally {
      setBusy(false);
    }
  };

  const retake = () => {
    if (shot) URL.revokeObjectURL(shot.url);
    setShot(null);
  };
  const use = () => { stop(); onCapture(shot.file); };
  const close = () => { stop(); if (shot) URL.revokeObjectURL(shot.url); onClose(); };

  const isSharp = sharp != null && sharp >= MIN_SHARPNESS;

  return (
    <div className="dialog-backdrop" role="dialog" aria-modal="true" aria-label="Take a photo of the report" onClick={close}>
      <div className="dialog capture-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-title">Take a photo of the report</div>
        <div className="dialog-sub">
          Lay the report flat in good light and fill the frame with the page. Hold the phone steady until the picture is sharp.
          A PDF from the lab is always more accurate than a photo.
        </div>

        {state === 'error' && (
          <div>
            <div className="alert alert-warning" role="alert">
              The camera could not be opened here. You can use your phone's camera app instead and attach the picture.
            </div>
            <input ref={fallbackRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files[0]; if (f) { stop(); onCapture(f); } }} />
            <div className="dialog-actions">
              <button type="button" className="btn btn-ghost" onClick={close}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={() => fallbackRef.current?.click()}>Open camera app</button>
            </div>
          </div>
        )}

        {state !== 'error' && !shot && (
          <>
            <div className="capture-stage">
              <video ref={videoRef} className="capture-video" playsInline muted />
              <div className="capture-frame" aria-hidden="true" />
            </div>
            <div className="capture-meter" role="status" aria-live="polite">
              {state === 'starting' && <span className="field-text">Opening the camera…</span>}
              {state === 'live' && sharp == null && <span className="field-text">Checking the picture…</span>}
              {state === 'live' && sharp != null && (
                <span className={`badge ${isSharp ? 'badge-active' : 'badge-pending'}`}>
                  {isSharp ? 'Sharp: ready to capture' : 'Too soft: hold steady or move a little closer'}
                </span>
              )}
            </div>
            <div className="dialog-actions">
              <button type="button" className="btn btn-ghost" onClick={close}>Cancel</button>
              <button type="button" className="btn btn-ghost" disabled={state !== 'live' || busy} onClick={capture}>Capture anyway</button>
              <button type="button" className="btn btn-primary" disabled={state !== 'live' || busy || !isSharp} onClick={capture}>
                {busy ? 'Capturing…' : 'Capture'}
              </button>
            </div>
          </>
        )}

        {shot && (
          <>
            <img className="capture-preview" src={shot.url} alt="Preview of the page you just captured" />
            {shot.problems.length > 0
              ? (
                <div className="alert alert-warning" role="alert" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                  {shot.problems.map((p) => <div key={p.code}>{p.message}</div>)}
                </div>
              )
              : <div className="alert alert-success" role="status">This photo looks sharp and large enough.</div>}
            <div className="dialog-actions">
              <button type="button" className="btn btn-ghost" onClick={retake}>Retake</button>
              <button type="button" className="btn btn-primary" onClick={use}>Use this photo</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
